import React, { useState, useEffect, useContext } from 'react';
import { basePageWrap, GradientContext } from '../BasePage';
import s from './OrganizationPage.module.scss';
import HeroOrganization from '../../components/HeroOrganization/HeroOrganization';
import OrganizationSubjects from '../../components/OrganizationSubjects';
import CardOrganizationList from '../../components/CardOrganizationList/CardOrganizationList';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import PaginationOrganization from '../../components/PaginationOrganization/PaginationOrganization';
import GradientBlurb from '../../components/GradientBlurb/GradientBlurb';
import StreamField from '../../components/StreamField/StreamField';
import { useInView } from 'react-intersection-observer';
import classNames from 'classnames';

const OrganizationPage = ({
  hero = {},
  filters = {},
  siteSetting,
  contentBlocks,
}) => {
  const activeLang =
    siteSetting?.languages?.find((l) => l.active)?.langCode || 'en';
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });
  const gradient = useContext(GradientContext);
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [activeMainCategories, setActiveMainCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [organizations, setOrganizations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const onSubjectsChange = (vals) => {
    setIsLoading(true);
    setSelectedSubjects(vals);
    setCurrentPage(1);
  };

  const onFiltersChange = (vals) => {
    setIsLoading(true);
    setSelectedFilters(vals);
    setCurrentPage(1);
  };
  useEffect(() => {
    setActiveMainCategories(selectedSubjects);

    const allowedValues = (filters.options || [])
      .filter((o) => selectedSubjects.includes(o.slug || o.value))
      .flatMap((o) => o.subCategories.map((s) => s.slug || s.value));

    const cleanedFilters = selectedFilters.filter((f) =>
      allowedValues.includes(f)
    );

    if (cleanedFilters.length !== selectedFilters.length) {
      setSelectedFilters(cleanedFilters);
    }
  }, [selectedSubjects, filters.options, selectedFilters]);

  useEffect(() => {
    const params = new URLSearchParams();

    if (selectedSubjects.length) {
      selectedSubjects.forEach((s) => params.append('subjects', s));
    }
    if (selectedFilters.length) {
      selectedFilters.forEach((f) => params.append('filters', f));
    }
    if (currentPage > 1) {
      params.append('page', currentPage);
    }

    window.history.replaceState({}, '', `?${params.toString()}`);
  }, [selectedSubjects, selectedFilters, currentPage]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageFromUrl = parseInt(params.get('page') || '1', 10);
    setCurrentPage(pageFromUrl);
  }, []);

  useEffect(() => {
    const fetchOrganizations = async () => {
      setIsLoading(true);

      const params = new URLSearchParams();
      selectedSubjects.forEach((s) => params.append('subjects', s));
      selectedFilters.forEach((f) => params.append('filters', f));
      params.append('page', currentPage);

      try {
        const res = await fetch(
          `/wt/api/v1/organizations/?${params.toString()}`,
          {
            headers: {
              'Accept-Language': activeLang,
            },
          }
        );
        const data = await res.json();

        setOrganizations(data.results || []);
        setTotalCount(data.count || 0);

        setTotalPages(data.total_pages || 1);
      } catch (err) {
        console.error('Failed to load organizations', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchOrganizations();
  }, [selectedSubjects, selectedFilters, currentPage]);

  const clearAllFilters = () => {
    setIsLoading(true);
    setSelectedSubjects([]);
    setSelectedFilters([]);
    setActiveMainCategories([]);
  };

  const isSearchActive =
    selectedSubjects.length > 0 || selectedFilters.length > 0;
  const classes = classNames(s.Root, {
    [s.Visible]: inView,
  });
  return (
    <div ref={ref} className={classes}>
      <HeroOrganization {...hero} />
      <GradientBlurb modifiers={['Organization']} />
      <GradientBlurb modifiers={['Organization--Left']} />
      <div className={s.Container} id="top-of-search">
        <Filters
          options={filters.options || []}
          selectedSubjects={selectedSubjects}
          setSelectedSubjects={onSubjectsChange}
          selectedFilters={selectedFilters}
          setSelectedFilters={onFiltersChange}
          activeMainCategories={activeMainCategories}
          setActiveMainCategories={setActiveMainCategories}
          onClear={clearAllFilters}
        />

        <div className={s.List}>
          <CardOrganizationList
            cards={organizations}
            totalCount={totalCount}
            isLoading={isLoading}
            showResultText={!isLoading && isSearchActive}
            showNoHits={
              !isLoading && isSearchActive && organizations.length === 0
            }
          />
        </div>

        <PaginationOrganization
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
      <div className={s.StreamField}>
        <StreamField items={contentBlocks} modifiers={['OrganizationPage']} />
      </div>
    </div>
  );
};

const Filters = ({
  options,
  selectedSubjects,
  setSelectedSubjects,
  selectedFilters,
  setSelectedFilters,
  activeMainCategories,
  setActiveMainCategories,
  onClear,
}) => {
  const { t } = useTranslation();

  return (
    <div className={s.Filters}>
      <OrganizationSubjects
        subjects={options}
        selectedSubjects={selectedSubjects}
        setSelectedSubjects={setSelectedSubjects}
        title={t('Organization.subjectsPlaceholder')}
      />
      {/* Hide for now. Might use later on */}
      {/* <div className={s.BottomWrapper}>
        <div className={s.SelectWrapper}>
          <h2 className={s.FilterTitle}>{t('Organization.selectTitle')}</h2>
          <MultiSelect
            options={options}
            value={selectedFilters}
            onChange={setSelectedFilters}
            placeholder={t('Organization.selectPlaceholder')}
            activeMainCategories={activeMainCategories}
            onSelectMainCategory={(mainCatValue) => {
              // se till att main category också är vald i subjects
              if (!selectedSubjects.includes(mainCatValue)) {
                setSelectedSubjects([...selectedSubjects, mainCatValue]);
              }
            }}
          />
        </div>
        {(selectedFilters.length > 0 || selectedSubjects.length > 0) && (
          <button onClick={onClear} className={s.ClearButton}>
            <div className={s.IconWrapper}>
              <Image
                width={24}
                height={24}
                src="/img/icon-trash.svg"
                alt=""
                className={s.Icon}
              />
            </div>
            <span className={s.ClearButtonText}>
              {t('Organization.clearAllFilters')}
            </span>
          </button>
        )}
      </div> */}
    </div>
  );
};

export default basePageWrap(OrganizationPage);
